/**
 * Possible alignment options for the popover relative to the trigger element.
 * - 'start': Aligns to the start edge of the trigger element
 * - 'center': Centers the popover on the trigger element
 * - 'end': Aligns to the end edge of the trigger element
 */
export const alignmentOptions = ['start', 'center', 'end'] as const;
export type Alignment = (typeof alignmentOptions)[number];

/**
 * Possible placement options for the popover relative to the trigger element.
 * - 'top': Places the popover above the trigger element
 * - 'bottom': Places the popover below the trigger element
 * - 'left': Places the popover to the left of the trigger element
 * - 'right': Places the popover to the right of the trigger element
 */
export const placementOptions = ['top', 'right', 'bottom', 'left'] as const;
export type Placement = (typeof placementOptions)[number];

/**
 * Offset configuration for popover positioning.
 * Can be a single number (applied to both x and y axes) or an object with specific x/y values.
 */
export type Offset = number | { x?: number; y?: number };

/**
 * Represents a position with x and y coordinates.
 */
export interface Position {
  x: number;
  y: number;
}

/**
 * Represents the dimensions and position of an element.
 */
export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

/**
 * Result of positioning calculations including final position and flip state
 */
export interface PositioningResult {
  position: Position;
  flip: {
    placement: Placement | null;
  };
}

/**
 * Additional configuration options for positioning calculations
 */
export interface PositioningOptions {
  /** Additional offset to apply to the position */
  offset?: Offset;
  /** Minimum distance to maintain from viewport edges */
  viewportMargin?: number;
}

/**
 * Extracts the x and y offset values from the Offset type.
 * Standardizes the offset format for easier usage in calculations.
 *
 * @param offset - The offset configuration (number or object)
 * @returns An object with x and y offset values
 */
const getOffsetValues = (offset: Offset = 0): { x: number; y: number } => {
  if (typeof offset === 'number') {
    return { x: offset, y: offset };
  }
  return { x: offset?.x ?? 0, y: offset?.y ?? 0 };
};

/**
 * Calculates the initial position of the popover based on the trigger element's position,
 * alignment, placement, and offset.
 *
 * This function handles the basic positioning logic without considering viewport constraints
 * or collision detection. The returned position is in document coordinates (includes scroll offset).
 *
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param popoverRect - The bounding rectangle of the popover element
 * @param placement - Where to place the popover relative to the trigger
 * @param align - How to align the popover with the trigger
 * @param options - Additional configuration options
 * @returns The calculated position for the popover in document coordinates
 */
export const calculateInitialPositionOfPopover = (
  triggerRect: Rect,
  popoverRect: Rect,
  placement: Placement,
  align: Alignment,
  options: PositioningOptions = {},
): Position => {
  const { offset = 0 } = options;

  const {
    top: triggerTop,
    left: triggerLeft,
    width: triggerWidth,
    height: triggerHeight,
  } = triggerRect;
  const { width: popoverWidth, height: popoverHeight } = popoverRect;

  const { x: offsetX, y: offsetY } = getOffsetValues(offset);

  // Get scroll position
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  // Adjust trigger position by scroll offset
  const adjustedTriggerTop = triggerTop + scrollY;
  const adjustedTriggerLeft = triggerLeft + scrollX;

  let x = 0;
  let y = 0;

  // Handle placement and alignment
  switch (placement) {
    case 'top':
    case 'bottom': {
      // Handle horizontal alignment for top/bottom placement
      switch (align) {
        case 'start':
          x = adjustedTriggerLeft;
          break;
        case 'end':
          x = adjustedTriggerLeft + triggerWidth - popoverWidth;
          break;
        case 'center':
        default:
          x = adjustedTriggerLeft + (triggerWidth - popoverWidth) / 2;
      }
      // Handle vertical placement
      y =
        placement === 'top'
          ? adjustedTriggerTop - popoverHeight - offsetY
          : adjustedTriggerTop + triggerHeight + offsetY;
      break;
    }
    case 'left':
    case 'right': {
      // Handle vertical alignment for left/right placement
      switch (align) {
        case 'start':
          y = adjustedTriggerTop;
          break;
        case 'end':
          y = adjustedTriggerTop + triggerHeight - popoverHeight;
          break;
        case 'center':
        default:
          y = adjustedTriggerTop + (triggerHeight - popoverHeight) / 2;
      }
      // Handle horizontal placement
      x =
        placement === 'left'
          ? adjustedTriggerLeft - popoverWidth - offsetX
          : adjustedTriggerLeft + triggerWidth + offsetX;
      break;
    }
  }

  return { x, y };
};

/**
 * Applies viewport constraints to ensure the popover stays within the viewport bounds.
 *
 * This function implements two key behaviors:
 * 1. Flipping: Reverses the placement (top↔bottom, left↔right) if the popover would overflow the viewport
 * 2. Boundary clamping: Adjusts the final position to keep the popover within viewport margins
 *
 * Note: This implementation prioritizes keeping the popover visible over preserving exact alignment.
 * When clamped to viewport boundaries, the popover may no longer maintain its original alignment relative to the trigger.
 *
 * @param position - The initial calculated position in document coordinates
 * @param popoverRect - The bounding rectangle of the popover element
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param placement - The original intended placement
 * @param options - Additional configuration options
 * @returns The adjusted position and flip information
 */
export const applyConstraintsToPopover = (
  position: Position,
  popoverRect: Rect,
  triggerRect: Rect,
  placement: Placement,
  options: PositioningOptions = {},
): PositioningResult => {
  const { offset = 0, viewportMargin = 8 } = options;

  const { x, y } = position;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const { width: popoverWidth, height: popoverHeight } = popoverRect;
  const { x: offsetX, y: offsetY } = getOffsetValues(offset);

  // Get scroll position
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  // Calculate viewport-relative positions for overflow detection
  const viewportX = x - scrollX;
  const viewportY = y - scrollY;

  let adjustedX = x;
  let adjustedY = y;
  let flippedPlacement: Placement | null = null;

  // Flip placement if popover would overflow the viewport
  if (placement === 'top' && viewportY < viewportMargin) {
    // Flip from top to bottom
    adjustedY = triggerRect.bottom + scrollY + offsetY;
    flippedPlacement = 'bottom';
  } else if (
    placement === 'bottom' &&
    viewportY + popoverHeight > viewportHeight - viewportMargin
  ) {
    // Flip from bottom to top
    adjustedY = triggerRect.top + scrollY - popoverHeight - offsetY;
    flippedPlacement = 'top';
  } else if (placement === 'left' && viewportX < viewportMargin) {
    // Flip from left to right
    adjustedX = triggerRect.right + scrollX + offsetX;
    flippedPlacement = 'right';
  } else if (
    placement === 'right' &&
    viewportX + popoverWidth > viewportWidth - viewportMargin
  ) {
    // Flip from right to left
    adjustedX = triggerRect.left + scrollX - popoverWidth - offsetX;
    flippedPlacement = 'left';
  }

  // Clamp position to keep popover within viewport boundaries
  adjustedX = Math.max(
    scrollX + viewportMargin,
    Math.min(
      adjustedX,
      scrollX + viewportWidth - popoverWidth - viewportMargin,
    ),
  );
  adjustedY = Math.max(
    scrollY + viewportMargin,
    Math.min(
      adjustedY,
      scrollY + viewportHeight - popoverHeight - viewportMargin,
    ),
  );

  return {
    position: { x: adjustedX, y: adjustedY },
    flip: {
      placement: flippedPlacement,
    },
  };
};

/**
 * Complete popover positioning function that combines initial positioning
 * and viewport constraints in one call.
 *
 * This is the main entry point for popover positioning. It first calculates the ideal position
 * based on placement and alignment preferences, then applies viewport constraints to ensure
 * the popover remains visible and accessible.
 *
 * @param triggerRect - The bounding rectangle of the trigger element
 * @param popoverRect - The bounding rectangle of the popover element
 * @param placement - Where to place the popover relative to the trigger
 * @param align - How to align the popover with the trigger
 * @param options - Additional configuration options
 * @returns The final position and flip information for the popover
 */
export const positionPopover = (
  triggerRect: Rect,
  popoverRect: Rect,
  placement: Placement,
  align: Alignment,
  options: PositioningOptions = {},
): PositioningResult => {
  // First calculate the initial position
  const initialPosition = calculateInitialPositionOfPopover(
    triggerRect,
    popoverRect,
    placement,
    align,
    options,
  );

  // Then apply constraints and get flip information
  return applyConstraintsToPopover(
    initialPosition,
    popoverRect,
    triggerRect,
    placement,
    options,
  );
};

/**
 * Computes the arrow's cross-axis offset inside the popover and clamps it to the popover's bounds.
 * Use after final positioning (including flips/clamping) so the arrow points at the trigger
 */
export type ComputeArrowOffsetParams = {
  triggerRect: Rect;
  popoverRect: Rect;
  position: Position;
  finalPlacement: Placement;
};

export const computeArrowOffset = ({
  triggerRect,
  popoverRect,
  position,
  finalPlacement,
}: ComputeArrowOffsetParams): number => {
  const triggerCenterX =
    triggerRect.left + triggerRect.width / 2 + window.scrollX;
  const triggerCenterY =
    triggerRect.top + triggerRect.height / 2 + window.scrollY;

  if (finalPlacement === 'top' || finalPlacement === 'bottom') {
    const raw = triggerCenterX - position.x; // x within popover coordinate space
    return Math.max(0, Math.min(raw, popoverRect.width));
  }

  // left | right
  const raw = triggerCenterY - position.y; // y within popover coordinate space
  return Math.max(0, Math.min(raw, popoverRect.height));
};
